import type { UserData, UUID } from '@cobliteam/common-types';
import useGlobalFilterStore from '@cobliteam/hooks/src/useGlobalFilterStore/useGlobalFilterStore';
import useAuthStore from '@cobliteam/hooks/src/useSession/store/useAuthStore';
import { useEffect, useRef } from 'react';

/**
 * Mirrors freshly-fetched `userData` into the peripheral stores that still read from it:
 * - `useAuthStore.setUserData` (persist snapshot used as `initialData` on next boot) — roda
 *   sempre que `userData` muda para manter o cache fresco.
 * - `useGlobalFilterStore.setVehicleGroupIds` (seed a partir das preferências) — roda **uma vez
 *   por `userId`**. Refetch em background (5min staleTime / window-focus) produz nova
 *   referência de UserData; sem esse ref, cada refetch clobbava a seleção viva do usuário no
 *   filtro global de grupos.
 *
 * Owns the side-effects that used to live inside `useGetUserData.queryFn`. Called exactly once
 * from `PrivateRouterProvider` so the sync runs regardless of how many consumers subscribe to
 * the query.
 */
const useSyncUserDataSideEffects = (userData: UserData | undefined, userId: UUID | undefined) => {
  const setVehicleGroupIdsState = useGlobalFilterStore((state) => state.setVehicleGroupIds);
  const seededForUserIdRef = useRef<UUID | null>(null);

  useEffect(() => {
    if (!(userData && userId)) return;
    if (seededForUserIdRef.current !== userId) {
      setVehicleGroupIdsState(userData.userPreferences?.vehicleGroupIds || []);
      seededForUserIdRef.current = userId;
    }
    useAuthStore.getState().setUserData(userId, userData);
  }, [userData, userId, setVehicleGroupIdsState]);
};

export default useSyncUserDataSideEffects;
